import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { AdminPageLayout, FormInput, FormSelect, FormTextArea, Section, Card, Grid, Col } from '../../components';
import { FormActionGroup } from '../../components/admin/patterns';
import { useToast } from '../../contexts/ToastContext';

const ProgramForm = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const isEdit = !!programId;

  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'recreational',
    sport: 'soccer',
    min_age: '',
    max_age: '',
    registration_fee: '',
    program_fee: '',
    league_level: '',
    skills: [],
    benefits: []
  });

  useEffect(() => {
    if (isEdit) {
      loadProgram();
    }
  }, [programId]);

  const loadProgram = async () => {
    try {
      setLoading(true);
      const program = await apiService.getProgram(programId);
      setFormData({
        name: program.name || '',
        description: program.description || '',
        category: program.category || 'recreational',
        sport: program.sport || 'soccer',
        min_age: program.min_age || '',
        max_age: program.max_age || '',
        registration_fee: program.registration_fee || '',
        program_fee: program.program_fee || '',
        league_level: program.league_level || '',
        skills: program.skills || [],
        benefits: program.benefits || []
      });
    } catch (err) {
      showError(err.message || 'Failed to load program');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Program name is required';
    }

    if (formData.min_age && formData.max_age) {
      const minAge = parseInt(formData.min_age);
      const maxAge = parseInt(formData.max_age);
      if (minAge > maxAge) {
        errors.min_age = 'Minimum age cannot be greater than maximum age';
        errors.max_age = 'Maximum age cannot be less than minimum age';
      }
    }

    if (formData.min_age && (parseInt(formData.min_age) < 4 || parseInt(formData.min_age) > 25)) {
      errors.min_age = 'Minimum age must be between 4 and 25';
    }

    if (formData.max_age && (parseInt(formData.max_age) < 4 || parseInt(formData.max_age) > 25)) {
      errors.max_age = 'Maximum age must be between 4 and 25';
    }

    if (formData.registration_fee && parseFloat(formData.registration_fee) < 0) {
      errors.registration_fee = 'Registration fee cannot be negative';
    }

    if (formData.program_fee && parseFloat(formData.program_fee) < 0) {
      errors.program_fee = 'Program fee cannot be negative';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});

    // Validate form
    if (!validateForm()) {
      showError('Please fix the errors below');
      return;
    }

    try {
      setLoading(true);

      // Clean up form data - convert empty strings to null for numeric fields
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        category: formData.category,
        sport: formData.sport,
        min_age: formData.min_age ? parseInt(formData.min_age) : undefined,
        max_age: formData.max_age ? parseInt(formData.max_age) : undefined,
        registration_fee: formData.registration_fee ? parseFloat(formData.registration_fee) : undefined,
        program_fee: formData.program_fee ? parseFloat(formData.program_fee) : undefined,
        league_level: formData.league_level.trim() || undefined,
        skills: formData.skills.filter(s => s.trim() !== ''),
        benefits: formData.benefits.filter(b => b.trim() !== '')
      };

      if (isEdit) {
        await apiService.updateProgram(programId, submitData);
        showSuccess('Program updated successfully');
      } else {
        await apiService.createProgram(submitData);
        showSuccess('Program created successfully');
      }

      navigate('/admin/programs');
    } catch (err) {
      // Parse field-level errors from API response
      if (err.message && err.message.includes('body.')) {
        const errors = {};
        const errorParts = err.message.split(', body.');
        errorParts.forEach(part => {
          const fieldMatch = part.match(/body\.([^:]+):/);
          if (fieldMatch) {
            const fieldName = fieldMatch[1];
            errors[fieldName] = 'Invalid value';
          }
        });
        setFieldErrors(errors);
        showError('Please fix the validation errors below');
      } else {
        showError(err.message || `Failed to ${isEdit ? 'update' : 'create'} program`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading && isEdit) {
    return (
      <AdminPageLayout
        title={isEdit ? 'EDIT PROGRAM' : 'NEW PROGRAM'}
        subtitle="Loading program information..."
        backPath="/admin/programs"
        maxWidth="1400px"
      >
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          Loading program...
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title={isEdit ? 'EDIT PROGRAM' : 'NEW PROGRAM'}
      subtitle="Create or edit training program"
      backPath="/admin/programs"
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <Section title="BASIC INFORMATION">
          <Card level={1}>
            <FormInput
              label="Program Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={fieldErrors.name || ''}
              placeholder="e.g., U-12 Competitive Soccer"
              required
            />

            <FormTextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Describe the program objectives and details"
            />

            <Grid columns={2} gap="2rem">
              <Col>
                <FormSelect
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  options={[
                    { value: 'recreational', label: 'Recreational' },
                    { value: 'competitive', label: 'Competitive' },
                    { value: 'travel', label: 'Travel' },
                    { value: 'academy', label: 'Academy' }
                  ]}
                  required
                />
              </Col>
              <Col>
                <FormSelect
                  label="Sport"
                  name="sport"
                  value={formData.sport}
                  onChange={handleChange}
                  options={[
                    { value: 'soccer', label: 'Soccer' },
                    { value: 'basketball', label: 'Basketball' },
                    { value: 'baseball', label: 'Baseball' },
                    { value: 'other', label: 'Other' }
                  ]}
                  required
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="AGE RESTRICTIONS">
          <Card level={1}>
            <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-secondary)' }}>
              Optional age constraints for this program. These will override club defaults.
            </p>

            <Grid columns={2} gap="2rem">
              <Col>
                <FormInput
                  type="number"
                  label="Minimum Age"
                  name="min_age"
                  value={formData.min_age}
                  onChange={handleChange}
                  error={fieldErrors.min_age || ''}
                  min="4"
                  max="25"
                  placeholder="e.g., 10"
                  helpText="Minimum age for this program (overrides club defaults)"
                />
              </Col>
              <Col>
                <FormInput
                  type="number"
                  label="Maximum Age"
                  name="max_age"
                  value={formData.max_age}
                  onChange={handleChange}
                  error={fieldErrors.max_age || ''}
                  min="4"
                  max="25"
                  placeholder="e.g., 12"
                  helpText="Maximum age for this program (overrides club defaults)"
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="FEES">
          <Card level={1}>
            <Grid columns={2} gap="2rem">
              <Col>
                <FormInput
                  type="number"
                  label="Registration Fee ($)"
                  name="registration_fee"
                  value={formData.registration_fee}
                  onChange={handleChange}
                  error={fieldErrors.registration_fee || ''}
                  min="0"
                  step="0.01"
                  placeholder="e.g., 50.00"
                />
              </Col>
              <Col>
                <FormInput
                  type="number"
                  label="Program Fee ($)"
                  name="program_fee"
                  value={formData.program_fee}
                  onChange={handleChange}
                  error={fieldErrors.program_fee || ''}
                  min="0"
                  step="0.01"
                  placeholder="e.g., 250.00"
                />
              </Col>
            </Grid>
          </Card>
        </Section>

        <Section title="ADDITIONAL DETAILS">
          <Card level={1}>
            <FormInput
              label="League Level"
              name="league_level"
              value={formData.league_level}
              onChange={handleChange}
              placeholder="e.g., Division 1, Premier League"
            />

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                Skills Developed
              </label>
              <p style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                List the skills or training focuses for this program. Each item on a new line.
              </p>
              <FormTextArea
                name="skills"
                value={formData.skills.join('\n')}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    skills: e.target.value.split('\n')
                  }));
                }}
                rows={4}
                placeholder="e.g., Ball Control&#10;Passing Accuracy&#10;Defensive Positioning&#10;Game Strategy"
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>
                Key Benefits
              </label>
              <p style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                List the key benefits or outcomes for participants. Each item on a new line.
              </p>
              <FormTextArea
                name="benefits"
                value={formData.benefits.join('\n')}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    benefits: e.target.value.split('\n')
                  }));
                }}
                rows={4}
                placeholder="e.g., Improved fitness and conditioning&#10;Team collaboration and leadership&#10;Confidence building&#10;Love of the sport"
              />
            </div>
          </Card>
        </Section>

        <FormActionGroup
          onCancel={() => navigate('/admin/programs')}
          onSave={handleSubmit}
          saveLabel={loading ? 'SAVING...' : (isEdit ? 'UPDATE PROGRAM' : 'CREATE PROGRAM')}
          loading={loading}
        />
      </form>
    </AdminPageLayout>
  );
};

export default ProgramForm;
